import { format, formatDistanceToNow } from 'date-fns'
import {
  History,
  Edit2,
  DollarSign,
  Calendar,
  User,
  ArrowRight,
  TrendingUp,
  FileText,
} from 'lucide-react'
import type { CSSProperties } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import type { HistoryRecord, HistoryDataType } from '@/types'

interface HistoryTimelineItemProps {
  record: HistoryRecord
}

export function HistoryTimelineItem({ record }: HistoryTimelineItemProps) {
  const getFieldIcon = (fieldName: string, dataType: HistoryDataType) => {
    // Special icons for common fields
    if (fieldName.toLowerCase().includes('owner')) {
      return <User className="h-4 w-4" aria-hidden="true" />
    }
    if (fieldName.toLowerCase().includes('stage')) {
      return <TrendingUp className="h-4 w-4" aria-hidden="true" />
    }
    if (fieldName.toLowerCase().includes('amount') || dataType === 'currency') {
      return <DollarSign className="h-4 w-4" aria-hidden="true" />
    }
    if (fieldName.toLowerCase().includes('date') || dataType === 'date') {
      return <Calendar className="h-4 w-4" aria-hidden="true" />
    }
    if (fieldName.toLowerCase().includes('status')) {
      return <Edit2 className="h-4 w-4" aria-hidden="true" />
    }

    // Default icon based on data type
    switch (dataType) {
      case 'currency':
        return <DollarSign className="h-4 w-4" aria-hidden="true" />
      case 'date':
        return <Calendar className="h-4 w-4" aria-hidden="true" />
      case 'reference':
        return <User className="h-4 w-4" aria-hidden="true" />
      default:
        return <FileText className="h-4 w-4" aria-hidden="true" />
    }
  }

  // GRK-BRAND-3: field-category icon circle from the extended categorical
  // palette (tint fill + solid glyph/border). No hover here, so inline style.
  const getFieldStyle = (fieldName: string): CSSProperties => {
    const f = fieldName.toLowerCase()
    const c = f.includes('owner')
      ? CATEGORICAL.navy
      : f.includes('stage')
        ? CATEGORICAL.evergreen
        : f.includes('amount') || f.includes('revenue')
          ? CATEGORICAL.teal
          : f.includes('date')
            ? CATEGORICAL.plum
            : f.includes('status')
              ? CATEGORICAL.steel
              : CATEGORICAL.slate
    return { backgroundColor: c.tint, color: c.solid, borderColor: c.solid }
  }

  const formatChangeDate = (date: string) => {
    const changeDate = new Date(date)
    const distance = formatDistanceToNow(changeDate, { addSuffix: true })
    const formatted = format(changeDate, 'MMM d, yyyy h:mm a')
    return { distance, formatted }
  }

  const { distance, formatted } = formatChangeDate(record.change_date)

  return (
    <article className="group" aria-label={`History change: ${record.field_label}`}>
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div
            className="flex-shrink-0 rounded-full p-2 border"
            style={getFieldStyle(record.field_name)}
          >
            {getFieldIcon(record.field_name, record.data_type)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Field changed */}
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-foreground">{record.field_label}</h3>
              <Badge variant="secondary" className="text-xs">
                {record.entity_type}
              </Badge>
            </div>

            {/* Value change */}
            <div className="mt-2 flex items-center gap-2 text-sm">
              <span
                className="px-2 py-1 rounded border line-through font-mono"
                style={{
                  backgroundColor: CATEGORICAL.raspberry.tint,
                  color: GRACKLE_COLORS.prussianBlue,
                  borderColor: CATEGORICAL.raspberry.solid,
                }}
                title="Previous value"
              >
                {record.formatted_old_value}
              </span>

              <ArrowRight
                className="h-4 w-4 text-muted-foreground flex-shrink-0"
                aria-hidden="true"
              />
              <span className="sr-only">changed to</span>

              <span
                className="px-2 py-1 rounded border font-mono"
                style={{
                  backgroundColor: GRACKLE_COLORS.pistachio,
                  color: GRACKLE_COLORS.prussianBlue,
                  borderColor: CATEGORICAL.evergreen.solid,
                }}
                title="New value"
              >
                {record.formatted_new_value}
              </span>
            </div>

            <Separator className="my-3" />

            {/* Metadata */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" aria-hidden="true" />
                <span>{record.changed_by_name || 'Unknown User'}</span>
              </div>

              <div className="flex items-center gap-1">
                <History className="h-3 w-3" aria-hidden="true" />
                <time dateTime={record.change_date} title={formatted}>
                  {distance}
                </time>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </article>
  )
}
